import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Paper,
  TablePagination,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { Content, Header, InfoCard, Page } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { PROJECTS_FORBIDDEN_ERROR, useProjectStore } from './useProjectStore';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ProjectCard } from './ProjectCard';
import { ProjectDeleteDialog } from './ProjectDeleteDialog';
import { useProjectSearch } from './useProjectSearch';
import { Project } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  cardValue: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
}));

export const ProjectsPage = () => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const config = useApi(configApiRef);
  const isProjectPreviewMode =
    config.getOptionalBoolean('ses.projectPreviewMode') ?? false;
  const {
    error,
    loading,
    projects,
    createProjectShell,
    assignMembers,
    updateProject,
    deleteProject,
    page,
    pageSize,
    total,
    goToPage,
  } = useProjectStore();
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [projectPendingDelete, setProjectPendingDelete] =
    useState<Project | null>(null);
  const [expandedActions, setExpandedActions] = useState<string | null>(null);
  const { filteredProjects, totalArtifacts, totalWorkItems, visibleProjects } =
    useProjectSearch(projects, query);

  let directoryContent;
  if (loading) {
    directoryContent = (
      <Paper variant="outlined" className={classes.emptyState}>
        <Typography variant="body2" className={classes.subtle}>
          {t('directory.loading')}
        </Typography>
      </Paper>
    );
  } else if (error === PROJECTS_FORBIDDEN_ERROR) {
    directoryContent = (
      <Paper variant="outlined" className={classes.emptyState}>
        <Typography variant="h6">{t('directory.noAccessTitle')}</Typography>
        <Typography variant="body2" className={classes.subtle}>
          {t('directory.noAccessDescription')}
        </Typography>
      </Paper>
    );
  } else if (error) {
    directoryContent = (
      <Paper variant="outlined" className={classes.emptyState}>
        <Typography variant="body2" className={classes.subtle}>
          {t('directory.loadError')}
        </Typography>
      </Paper>
    );
  } else if (projects.length === 0) {
    // User has no projects at all
    directoryContent = (
      <Paper variant="outlined" className={classes.emptyState}>
        <Typography variant="h6">{t('directory.noProjectsTitle')}</Typography>
        <Typography variant="body2" className={classes.subtle}>
          {t('directory.noProjectsDescription')}
        </Typography>
        <Box mt={2}>
          <Button
            color="primary"
            variant="contained"
            disabled={loading}
            onClick={() => setCreateOpen(true)}
          >
            {t('actions.createProject')}
          </Button>
        </Box>
      </Paper>
    );
  } else if (filteredProjects.length === 0) {
    // Has projects but no search results
    directoryContent = (
      <Paper variant="outlined" className={classes.emptyState}>
        <Typography variant="h6">{t('directory.emptyTitle')}</Typography>
        <Typography variant="body2" className={classes.subtle}>
          {t('directory.emptyDescription')}
        </Typography>
      </Paper>
    );
  } else {
    directoryContent = (
      <Grid container spacing={3}>
        {filteredProjects.map(project => (
          <Grid item md={6} xs={12} key={project.id}>
            <ProjectCard
              expanded={expandedActions === project.id}
              isProjectPreviewMode={isProjectPreviewMode}
              project={project}
              onDelete={() => {
                setProjectPendingDelete(project);
                setExpandedActions(null);
              }}
              onToggleActions={() =>
                setExpandedActions(current =>
                  current === project.id ? null : project.id,
                )
              }
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Page themeId="home">
      <Header title={t('list.title')} subtitle={t('list.subtitle')}>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || error === PROJECTS_FORBIDDEN_ERROR}
          onClick={() => setCreateOpen(true)}
        >
          {t('actions.createProject')}
        </Button>
      </Header>
      <Content>
        <Grid container spacing={3}>
          <Grid item md={4} xs={12}>
            <InfoCard
              variant="gridItem"
              title={t('metrics.visibleProjectsTitle')}
            >
              <Typography variant="h3" className={classes.cardValue}>
                {visibleProjects.length}
              </Typography>
              <Typography variant="body2" className={classes.subtle}>
                {t('metrics.visibleProjectsDescription')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item md={4} xs={12}>
            <InfoCard variant="gridItem" title={t('metrics.workItemsTitle')}>
              <Typography variant="h3" className={classes.cardValue}>
                {totalWorkItems}
              </Typography>
              <Typography variant="body2" className={classes.subtle}>
                {t('metrics.workItemsDescription')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item md={4} xs={12}>
            <InfoCard
              variant="gridItem"
              title={t('metrics.contextArtifactsTitle')}
            >
              <Typography variant="h3" className={classes.cardValue}>
                {totalArtifacts}
              </Typography>
              <Typography variant="body2" className={classes.subtle}>
                {t('metrics.contextArtifactsDescription')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12}>
            <InfoCard title={t('directory.title')}>
              <Box mb={3}>
                <Typography variant="body2" className={classes.subtle}>
                  {isProjectPreviewMode
                    ? t('directory.previewModeActive')
                    : t('directory.previewDisabled')}
                </Typography>
              </Box>
              <Box mb={3}>
                <TextField
                  fullWidth
                  label={t('directory.searchLabel')}
                  placeholder={t('directory.searchPlaceholder')}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  variant="outlined"
                />
              </Box>
              {directoryContent}
              {!isProjectPreviewMode &&
                !loading &&
                !error &&
                projects.length > 0 && (
                  <Box mt={2}>
                    <TablePagination
                      component="div"
                      count={total}
                      page={page - 1}
                      onPageChange={(_, newPage) => goToPage(newPage + 1)}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[pageSize]}
                      labelRowsPerPage={t('directory.rowsPerPage')}
                      labelDisplayedRows={({ from, to, count }) =>
                        t('directory.paginationLabel', { from, to, count })
                      }
                    />
                  </Box>
                )}
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreateProject={createProjectShell}
        onUpdateProject={updateProject}
        onAssignMembers={assignMembers}
      />
      <ProjectDeleteDialog
        project={projectPendingDelete}
        deleteProject={deleteProject}
        error={error}
        onClose={() => setProjectPendingDelete(null)}
      />
    </Page>
  );
};

export const projectsPage = <ProjectsPage />;
